import { FC, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { RootState } from 'store/rootReducer';
import { patchPublicering } from 'store/utkastSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { BebyggelseobjektUtkast } from 'models';

import { formatDateWithTime } from 'utilities/dateUtilities';

const PubliceraUtkastDialog: FC<{
  id: string;
  onSuccess: () => void;
}> = ({ id, onSuccess }): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { unsetDialog, setDialog } = useDialog();
  const { requestStatus } = useAppSelector((state: RootState) => state.utkast);

  const handlePublicering = async (): Promise<void> => {
    if (id && requestStatus !== 'pending') {
      const response = await dispatch(patchPublicering(id));
      if (response.meta.requestStatus === 'fulfilled') {
        const utkast = response.payload as BebyggelseobjektUtkast;
        const proceed = (navigateTo: string) => {
          onSuccess();
          unsetDialog();
          navigate(navigateTo, { replace: true });
        };

        if (utkast?.registrering?.granskning_arendenummer) {
          proceed(`/utkast/${id}`);
          setDialog(RaaDialog, {
            header: 'Mottagen för granskning',
            close: unsetDialog,
            proceed: unsetDialog,
            ariaDescribedBy: 'dialog-description',
            children: (
              <div className="tw-mt-8">
                <p id="dialog-description">
                  Vi har tagit emot informationen och kommer att publicera den efter godkänd granskning av
                  personuppgifter och sekretess.
                </p>
                <p>
                  Ärendenummer: {utkast.registrering.granskning_arendenummer}, mottaget{' '}
                  {formatDateWithTime(utkast.registrering.status_datum_inskickad)}
                </p>
              </div>
            )
          });
        } else {
          proceed(`/bebyggelseobjekt/${id}`);
          setDialog(RaaDialog, {
            header: 'Publiceringen godkändes',
            close: unsetDialog,
            proceed: unsetDialog,
            ariaDescribedBy: 'dialog-description',
            children: (
              <p id="dialog-description" className="tw-mt-8">
                Ingen manuell granskning krävdes.
              </p>
            )
          });
        }
      }
    }
  };

  return (
    <RaaDialog
      header="Skicka till publicering?"
      proceed={handlePublicering}
      cancel={unsetDialog}
      close={requestStatus === 'idle' ? unsetDialog : undefined}
      showLoadingSpinner={requestStatus === 'pending'}
      proceedButtonText="publicera"
      cancelButtonText="avbryt"
      ariaDescribedBy="dialog-description"
    >
      <p id="dialog-description" className="tw-my-4">
        Inskickad information blir offentlig handling och kommer att publiceras efter Riksantikvarieämbetets granskning.
      </p>
    </RaaDialog>
  );
};

export default PubliceraUtkastDialog;
